import { View, Text } from "@src/components";
import { memo, useCallback, useMemo } from "react";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import SegmentedSelector from "@src/components/SegmentedSelector";
import { capitalize } from "@src/utils/capitalize";
import { PressableScale } from "pressto";
import { Icon, IconNameType } from "@src/components/Icon";
import { Pressable } from "react-native";
import { isWeb } from "@src/utils/web-helpers";

type ListRoleSelectorItem = {
  id: string;
  name: string;
  role: "manager" | "viewer";
  email?: string;
  phoneNumber?: string;
};

type ListRoleSelectorProps = {
  items: ListRoleSelectorItem[];
  onRoleChange: (itemId: string, role: "manager" | "viewer") => void;
  iconName: IconNameType;
  onPressIcon?: (itemId: string) => void;
  onPressItem?: (itemId: string) => void;
};

const ListRoleSelectorRow = memo(
  ({
    item,
    isLast,
    onRoleChange,
    onPressIcon,
    iconName,
    onPressItem,
  }: {
    item: ListRoleSelectorItem;
    isLast: boolean;
    onRoleChange: (itemId: string, role: "manager" | "viewer") => void;
    onPressIcon?: (itemId: string) => void;
    iconName: IconNameType;
    onPressItem?: (itemId: string) => void;
  }) => {
    const { theme } = useUnistyles();
    const isLastItemStyle = useMemo(() => ({ borderBottomWidth: isLast ? 0 : 1 }), [isLast]);
    const options = useMemo(() => [{ title: "Manager" }, { title: "Viewer" }], []);

    const handleSelectOption = useCallback(
      (option: string) => {
        onRoleChange(item.id, option.toLowerCase() as "manager" | "viewer");
      },
      [onRoleChange, item.id],
    );

    const handlePressIcon = useCallback(() => {
      onPressIcon?.(item.id);
    }, [onPressIcon, item.id]);

    const handlePressItem = useCallback(() => {
      onPressItem?.(item.id);
    }, [onPressItem, item.id]);

    return (
      <Pressable onPress={handlePressItem}>
        <View
          bg="surfaceCard"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          style={[styles.item, isLastItemStyle]}
        >
          <View gap="xs" flex={1}>
            <Text textStyle="sm/medium" numberOfLines={3}>
              {item.name || item.email}
            </Text>
            {item.email && item.name && (
              <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                {item.email}
              </Text>
            )}
            {item.phoneNumber && (
              <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                {item.phoneNumber}
              </Text>
            )}
          </View>
          <View flexDirection="row" alignItems="center" gap="md">
            <SegmentedSelector
              optionsLabels={options}
              selectedOption={capitalize(item.role)}
              onSelectOption={handleSelectOption}
              containerStyle={styles.roleSelectorContainer}
              activeBoxStyle={styles.roleSelectorActiveBox}
              width={isWeb ? 200 : undefined}
            />
            <PressableScale onPress={handlePressIcon}>
              <Icon name={iconName} color={theme.colors.textSecondary} />
            </PressableScale>
          </View>
        </View>
      </Pressable>
    );
  },
);

export const ListRoleSelector = memo(
  ({ items, onRoleChange, onPressIcon, iconName, onPressItem }: ListRoleSelectorProps) => {
    return (
      <View bg="surfaceCard" style={styles.container}>
        {items.map((item, index) => (
          <ListRoleSelectorRow
            key={item.id}
            item={item}
            isLast={index === items.length - 1}
            onRoleChange={onRoleChange}
            onPressIcon={onPressIcon}
            iconName={iconName}
            onPressItem={onPressItem}
          />
        ))}
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    overflow: "hidden",
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xs,
  },
  item: {
    paddingVertical: theme.spacing["3xl"],
    borderBottomColor: theme.colors.borderCard,
    gap: theme.spacing.xxs,
    paddingRight: theme.spacing.md,
  },
  roleSelectorContainer: {
    backgroundColor: "transparent",
  },
  roleSelectorActiveBox: {
    backgroundColor: theme.colors.surface2.hover,
  },
}));
